import { useState } from "react";
import Link from "next/link";
import styled from "styled-components";
import { CommonAgentSummary, CommonSessionDetails } from "../types";

import { useLocalStorage } from "../util/localStorage";
import {
  AttributesView,
  toAttributes,
  Inspector,
  Toolbar,
  FixedToolbarItem,
  CardGrid,
  Card,
  FlexToolbarItem,
} from "@bagel-web/components";
import { ButtonGroup, Table, ToggleButton } from "react-bootstrap";
import AgentDetails from "./AgentDetails";
import AgentIcon from "./AgentIcon";
import { getFriendlyAgentName } from "../util";

const AgentNameLink = styled.a`
  margin-bottom: 4px;
  font-weight: bold;
`;

const StyledAgentIcon = styled(AgentIcon)`
  font-size: 1.5rem;
`;

const AgentName = ({
  agent,
  session,
}: {
  agent: CommonAgentSummary;
  session: CommonSessionDetails;
}) => {
  return (
    <AgentNameLink href={`/sessions/${session.id}/agent/${agent.id}`}>
      <code>{agent.id}</code>
    </AgentNameLink>
  );
};

const SESSION_AGENT_VIEW_MODE_LOCAL_STORAGE_KEY = "session-agent-view-mode";

const AgentList = ({
  session,
  showApiRequestsLink = false,
  showMomentsLink = false,
}: {
  session: CommonSessionDetails;
  showApiRequestsLink?: boolean;
  showMomentsLink?: boolean;
}) => {
  const [showAgentGrid, setShowAgentGrid] = useState(
    useLocalStorage<boolean>().getItem(
      SESSION_AGENT_VIEW_MODE_LOCAL_STORAGE_KEY
    ) || false
  );

  const handleChangeShowGrid = () => {
    useLocalStorage<boolean>().setItem(
      SESSION_AGENT_VIEW_MODE_LOCAL_STORAGE_KEY,
      !showAgentGrid
    );
    setShowAgentGrid(!showAgentGrid);
  };

  let numLinks = 1;
  if (showApiRequestsLink) {
    numLinks++;
  }
  if (showMomentsLink) {
    numLinks++;
  }

  return (
    <>
      <Toolbar>
        <FlexToolbarItem />
        <FixedToolbarItem>
          <ButtonGroup aria-label="View mode">
            <ToggleButton
              type="radio"
              id="agent-view-mode-grid"
              value="grid"
              variant="outline-secondary"
              className="bi-grid-fill"
              checked={showAgentGrid}
              onChange={handleChangeShowGrid}
            />
            <ToggleButton
              type="radio"
              id="agent-view-mode-list"
              value="list"
              variant="outline-secondary"
              checked={!showAgentGrid}
              onChange={handleChangeShowGrid}
              className="bi-list"
            />
          </ButtonGroup>
        </FixedToolbarItem>
      </Toolbar>

      {showAgentGrid && (
        <CardGrid unitWidth={600}>
          {session?.agents.map((agent) => {
            return (
              <Card key={agent.id} id={agent.id}>
                <>
                  <AgentDetails agent={agent} showName={true} />

                  {showApiRequestsLink && (
                    <Link
                      href={`/sessions/${session.id}/api-requests?agentId=${agent.id}`}
                      className="card-link"
                    >
                      API Requests
                    </Link>
                  )}

                  {showMomentsLink && (
                    <Link
                      href={`/sessions/${session.id}/moments?agentId=${agent.id}`}
                      className="card-link"
                    >
                      Moments
                    </Link>
                  )}

                  <Link
                    href={`/sessions/${session.id}/agent/${agent.id}/event-stream`}
                    className="card-link"
                  >
                    Message Stream
                  </Link>
                </>
              </Card>
            );
          })}
        </CardGrid>
      )}
      {!showAgentGrid && (
        <Table>
          <thead>
            <tr>
              <th></th>
              <th>Name</th>
              <th>Agent ID</th>
              <th>Metadata</th>
              <th colSpan={numLinks}>Links</th>
            </tr>
          </thead>
          <tbody>
            {session?.agents.map((agent) => {
              return (
                <tr key={agent.id} style={{ verticalAlign: "middle" }}>
                  <td>
                    <StyledAgentIcon agent={agent} />
                  </td>
                  <td>{getFriendlyAgentName(agent)}</td>
                  <td>
                    {
                      <Inspector
                        showLoading={false}
                        View={AgentDetails}
                        viewProps={{ agent }}
                      >
                        <AgentName agent={agent} session={session} />
                      </Inspector>
                    }
                  </td>
                  <td>
                    <AttributesView attributes={toAttributes(agent.metadata)} />
                  </td>
                  {showApiRequestsLink && (
                    <td>
                      <Link
                        href={`/sessions/${session.id}/api-requests?agentId=${agent.id}`}
                      >
                        API Requests
                      </Link>
                    </td>
                  )}
                  {showMomentsLink && (
                    <td>
                      <Link
                        href={`/sessions/${session.id}/moments?agentId=${agent.id}`}
                      >
                        Moments
                      </Link>
                    </td>
                  )}
                  <td>
                    <Link
                      href={`/sessions/${session.id}/agent/${agent.id}/event-stream`}
                    >
                      Message Stream
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default AgentList;
